import { useQueryClient } from '@tanstack/react-query';
import type { CellContext, ColumnDef } from '@tanstack/table-core';

import { DataTableColumnHeader } from '@/components/DataTable/Columns/DataTableColumnHeader';
import { AppUserActionButton } from '@/features/app-user/components/SuggestedActionColumn/AppUserActionButton';
import type { AppUserAction } from '@/features/app-user/types';
import {
  type SuggestedAction,
  suggestedAction,
} from '@/features/app-user/types/action';

type AppUserActionColumnProps = {
  appIntegration?: {
    appIntegrationId: string;
  };
  user?: {
    id: string;
  };
  person?: {
    id: string;
  };
  software: {
    name: string;
  };
  action?: {
    type: SuggestedAction;
    disabled: boolean;
    describe?: string;
  };
};

export function AppUserPrimaryActionColumn<
  T extends AppUserActionColumnProps,
>() {
  const queryClient = useQueryClient();

  return {
    id: 'primary-action',
    accessorKey: 'primary-action',
    enableSorting: false,
    size: 170,
    meta: { i18nKey: 'action' as const },
    header: (props) => <DataTableColumnHeader {...props} />,
    cell: ({ row: { original } }: CellContext<T, unknown>) => {
      const { appIntegration, user, person, software, action } = original;

      if (!action || !appIntegration) {
        return '-';
      }

      if (
        action.type === suggestedAction.GROUP_JOIN ||
        action.type === suggestedAction.GROUP_LEAVE
      ) {
        return '-';
      }

      return (
        <AppUserActionButton
          appIntegrationId={appIntegration.appIntegrationId}
          appUserId={user?.id}
          personId={person?.id}
          appName={software.name}
          action={action as AppUserAction}
          onSuccess={() => {
            queryClient.invalidateQueries({
              queryKey: ['alerts'],
            });
          }}
        />
      );
    },
  } satisfies ColumnDef<T>;
}
